import { useEffect, useMemo } from "react";
import * as yup from "yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { ButtonContainer } from "../../components/ButtonContainer";
import { PageContainer } from "../../components/PageContainer";
import { useMutation } from "@apollo/client";
import { DELETE_CONFIG, UPDATE_CONFIG } from "./graphql";
import { Config, ConfigInputType } from "../../__generated__/graphql";
import { toast } from "react-toastify";
import { UpdateMiscConfigButton } from "../links/updateMiscConfig";
import { TimeZonePicker } from "../../components/TimezonePicker";
import { ErrorRenderer } from "../../components/ErrorRenderer";
import { ScreenSelector } from "../settings/ScreenSelector";
import {
  ConfigFormRow,
  mergeConfigWithDefaults,
} from "./defaultSettings";

yup.addMethod(yup.object, "uniqueProperty", function (propertyName, message) {
  return this.test("unique", message, function (value) {
    if (!value || !value[propertyName]) {
      return true;
    }

    if (
      this.parent
        // @ts-ignore
        .filter((v) => v !== value)
        // @ts-ignore
        .some((v) => v[propertyName] === value[propertyName])
    ) {
      throw this.createError({
        path: `${this.path}.${propertyName}`,
      });
    }

    return true;
  });
});

const schema = yup
  .object({
    config: yup.array().of(
      yup
        .object({
          keyId: yup.string(),
          value: yup.string(),
        })
        .test("unique", "keyId must be unique", function (value) {
          const propertyName = "keyId";
          if (!value || !value[propertyName]) {
            return true;
          }

          const { path } = this;
          const options = [...this.parent];
          const currentIndex = options.indexOf(value);

          const subOptions = options.slice(0, currentIndex);

          if (
            subOptions.some(
              (option) => option[propertyName] === value[propertyName]
            )
          ) {
            throw this.createError({
              path: `${path}.${propertyName}`,
              message: "keyId must be unique",
            });
          }

          return true;
        })
    ),
  })
  .required();

export const ManageConfigTable = ({
  data,
  refetch,
}: {
  data: Config[];
  refetch: () => void;
}) => {
  const SPECIAL_FIELDS = new Set(["TIMEZONE", "ENABLED_SCREENS"]);
  const mergedConfig = useMemo(
    () => mergeConfigWithDefaults(data as { key?: string; value?: unknown }[]),
    [data],
  );
  const mergedConfigKey = useMemo(
    () => JSON.stringify(mergedConfig),
    [mergedConfig],
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<{ config: ConfigFormRow[] }>({
    resolver: yupResolver(schema),
    defaultValues: {
      config: mergedConfig,
    },
  });

  useEffect(() => {
    const nextConfig = JSON.parse(mergedConfigKey) as ConfigFormRow[];
    reset({
      config: nextConfig,
    });
  }, [mergedConfigKey, reset]);

  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "config", // unique name for your Field Array
  });

  const [updateConfig] = useMutation(UPDATE_CONFIG, {
    awaitRefetchQueries: true,
    refetchQueries: ["config"],
    onCompleted: () => {
      refetch();
      toast("Update complete");
    },
  });

  const [deleteConfig] = useMutation(DELETE_CONFIG, {
    awaitRefetchQueries: true,
    refetchQueries: ["config"],
    onCompleted: () => {
      refetch();
      toast("Delete complete");
    },
  });

  const onSubmit = (values: { config: ConfigFormRow[] }) => {
    const settings: ConfigInputType[] = (values?.config ?? [])
      .filter((item) => item.keyId?.trim().length > 0 && !item.isDefault)
      .map((item) => {
        return {
          key: item.keyId.trim(),
          value: item.value ?? "",
        };
      });

    updateConfig({
      variables: {
        settings,
      },
    });
  };

  return (
    <PageContainer title="Config">
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", width: "80%" }}
      >
        <ButtonContainer>
          <Button type="submit" variant="contained" style={{ height: "100%" }}>
            Save
          </Button>

          <Button
            onClick={() =>
              append({
                keyId: "",
                value: "",
              })
            }
            color="success"
            variant="contained"
            style={{ height: "100%" }}
          >
            Add New
          </Button>
          <UpdateMiscConfigButton />
        </ButtonContainer>
        <TableContainer component={Paper} style={{ overflow: "visible" }}>
          <Table
            sx={{ minWidth: 650 }}
            aria-label="simple table"
            style={{ overflow: "visible" }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="left">key</TableCell>
                <TableCell align="left">value</TableCell>
                <TableCell align="left">action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((item, index) => {
                  const hasSpecialField = SPECIAL_FIELDS.has(item.keyId ?? "");
                  const shouldUseSpecialField =
                    hasSpecialField && !item.isDefault;
                  return (
                    <TableRow
                      key={item.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      style={{ overflow: "visible" }}
                    >
                      <TableCell align="left">
                        <TextField
                          fullWidth
                          disabled={!!item.keyId}
                          id="outlined-basic"
                          label="key"
                          variant="outlined"
                          defaultValue={item.keyId}
                          {...register(`config.${index}.keyId`)}
                        />
                        {/* @ts-ignore */}

                        {errors.config?.[index]?.keyId?.message}
                      </TableCell>
                      <TableCell align="left">
                        {shouldUseSpecialField && item.keyId === "TIMEZONE" && (
                          <Controller
                            {...register(`config.${index}.value`)}
                            name={`config.${index}.value`}
                            control={control}
                            render={({
                              field: { onChange, onBlur, value, ref },
                            }) => (
                              // @ts-ignore
                              <TimeZonePicker
                                onChange={onChange}
                                // @ts-ignore
                                value={value}
                              />
                            )}
                          />
                        )}
                        {shouldUseSpecialField &&
                          item.keyId === "ENABLED_SCREENS" && (
                          <Controller
                            {...register(`config.${index}.value`)}
                            name={`config.${index}.value`}
                            control={control}
                            render={({
                              field: { onChange, onBlur, value, ref },
                            }) => (
                              <ScreenSelector
                                items={value ?? ""}
                                onChange={(value) => {
                                  onChange(value);
                                }}
                              />
                            )}
                          />
                        )}

                        {!shouldUseSpecialField && (
                          <TextField
                            fullWidth
                            id="outlined-basic"
                            label="value"
                            variant="outlined"
                            defaultValue={item.value}
                            {...register(`config.${index}.value`, {
                              onChange: () =>
                                setValue(`config.${index}.isDefault`, false),
                            })}
                          />
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Button
                          onClick={() => {
                            // @ts-ignore
                            if (item.keyId === "") {
                              remove(index);
                            } else {
                              deleteConfig({ variables: { key: item.keyId } });
                            }
                          }}
                          color="error"
                          variant="contained"
                          style={{
                            // marginTop: 18,
                            marginBottom: 20,
                            // marginRight: 8,
                          }}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <ErrorRenderer errors={errors} />
      </form>
    </PageContainer>
  );
};
